import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", short: "", legal_entity: "", specialty: "", ehr_system: "Other EHR (CSV)", color: "#0B3A82", timezone: "America/New_York" };
const ADMIN0 = { self: false, email: "", first_name: "", last_name: "" };

function Field({ id, label, value, onChange, ...p }) {
  return <div><Label htmlFor={id} className="text-xs">{label}</Label><Input id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} {...p} /></div>;
}

function Result({ r, onClose }) {
  const a = r.first_admin;
  return (
    <div className="space-y-3 text-sm" data-testid="org-created-result">
      <p><b>{r.name}</b> is ready.</p>
      {a.kind === "self" && <p>You are its first administrator (recorded in the audit log). Add its staff from Admin, then remove yourself if you should not keep access.</p>}
      {a.kind === "added" && <p><b>{a.name}</b> ({a.email}) already had an ACE eSIGN sign-in and is now its administrator. Email notice: {a.email_status}.</p>}
      {a.kind === "new" && <p>A sign-in was created for <b>{a.name}</b> ({a.email}). Give them the temporary password <code className="bg-slate-100 px-1 rounded" data-testid="org-created-temp-pw">{a.temp_password}</code> yourself; it is never emailed. Email notice: {a.email_status}.</p>}
      <DialogFooter><Button onClick={onClose} data-testid="org-created-close">Done</Button></DialogFooter>
    </div>
  );
}

export default function NewOrgDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [a, setA] = useState(ADMIN0);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const close = () => { setF(EMPTY); setA(ADMIN0); setRes(null); onOpenChange(false); };
  const create = async () => {
    setBusy(true);
    try { const r = await api.post("/enterprise/organizations", { ...f, first_admin: a }); setRes({ ...r.data, name: f.name }); onCreated(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-xl" data-testid="org-new-dialog">
        <DialogHeader><DialogTitle>New organization</DialogTitle>
          <DialogDescription>Creates a separate organization with its own patients, packets and audit log, and copies every active shared library form into it.</DialogDescription></DialogHeader>
        {res ? <Result r={res} onClose={close} /> : (<>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field id="org-new-name" label="Organization name *" value={f.name} onChange={set("name")} maxLength={80} />
            <Field id="org-new-short" label="Short name" value={f.short} onChange={set("short")} maxLength={12} placeholder="Shown where space is tight" />
            <div className="sm:col-span-2"><Field id="org-new-entity" label="Legal entity *" value={f.legal_entity} onChange={set("legal_entity")} maxLength={120} /></div>
            <Field id="org-new-specialty" label="Specialty" value={f.specialty} onChange={set("specialty")} placeholder="e.g. primary care" />
            <Field id="org-new-ehr" label="EHR system" value={f.ehr_system} onChange={set("ehr_system")} />
            <Field id="org-new-color" label="Brand colour" type="color" value={f.color} onChange={set("color")} className="h-9 p-1" />
          </div>
          <div className="border rounded-lg p-3 space-y-2 bg-slate-50" data-testid="org-new-admin">
            <p className="text-sm font-semibold">First organization administrator</p>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={a.self} onChange={(e) => setA({ ...a, self: e.target.checked })} data-testid="org-new-admin-self" />Make me the first administrator (recorded in the audit log)</label>
            {!a.self && <div className="grid sm:grid-cols-3 gap-2">
              <div className="sm:col-span-3"><Field id="org-new-admin-email" label="Email *" value={a.email} onChange={(v) => setA({ ...a, email: v })} type="email" /></div>
              <Field id="org-new-admin-first" label="First name" value={a.first_name} onChange={(v) => setA({ ...a, first_name: v })} />
              <Field id="org-new-admin-last" label="Last name" value={a.last_name} onChange={(v) => setA({ ...a, last_name: v })} />
              <p className="text-xs text-slate-500 sm:col-span-3 self-center">Names are needed only if this person has no ACE eSIGN sign-in yet. A new sign-in gets the shared temporary password.</p>
            </div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={close}>Cancel</Button>
            <Button className="btn-brand" disabled={busy || !f.name.trim() || !f.legal_entity.trim() || (!a.self && !a.email.trim())} onClick={create} data-testid="org-new-create">{busy ? "Creating..." : "Create organization"}</Button></DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
